import { GQLPrescriptionByPrescriptionCodePayload } from "@rx-cs-system/schema";
import { ComponentProps } from "react";
import styled from "styled-components";
import { StatusLabelForCsSystemType, SnackBarType } from "../../../types";
import { StatusLabelMap } from "../../../utils/prescription";
import { hexToRGB } from "../../../utils/style";

import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";
import { Button } from "../../atoms/Button";
import { List, ListItem } from "../../atoms/List";
import { SearchInput } from "../../atoms/SearchInput";
import { Snackbar } from "../../atoms/Snackbar";
import { Typography } from "../../atoms/Typography";
import { PageBody } from "../PageBody";

/**
 * 処方キャンセルページ
 */

export type PrescriptionCancelProps = {
  isCancelPrescription: boolean;
  isLoading: boolean;
  prescription?: GQLPrescriptionByPrescriptionCodePayload | null;
  snackBar: SnackBarType;
  onSearchClick: (keyword: string) => void;
  onSubmitClick: (code: string) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 40px;
  height: 100%;
`;

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding-bottom: 20px;
  display: flex;
  ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const LeftBlock = styled.div`
  flex: 1;
  padding-right: 40px;
`;

const MemberName = styled.div`
  display: flex;
  align-items: center;
`;

const RightBlock = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const SubmitButtonWrapper = styled.div`
  width: 100%;
  height: 50px;
  max-width: 250px;
  margin: 10px auto 0;
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

export function PrescriptionCancelPage({
  isCancelPrescription,
  isLoading,
  prescription,
  snackBar,
  onSearchClick,
  onSubmitClick,
}: PrescriptionCancelProps) {
  const searchInput = (
    <SearchInput
      clear={false}
      placeholder="処方コードを入力"
      onSearch={onSearchClick}
      onReset={() => {}}
      disabled={isCancelPrescription}
    />
  );

  return (
    <>
      <PageBody title="処方キャンセル" searchInput={searchInput}>
        <Container>
          {typeof prescription === "undefined" ? (
            <Typography>
              右上のテキストフィールドに処方コードを入力し、検索ボタンをクリックしてください。
            </Typography>
          ) : isLoading ? (
            <Typography>処方情報取得中...</Typography>
          ) : prescription === null ? (
            <Typography>該当の処方コードは見つかりませんでした。</Typography>
          ) : (
            <>
              <Inner>
                <Typography size={2}>
                  以下の処方情報で間違いないか確認し、「処方強制キャンセル」ボタンをクリックしてください。
                </Typography>
              </Inner>
              <ContentWrapper>
                <Inner>
                  <LeftBlock>
                    <MemberName>
                      <Typography size={5} breakWord>
                        {prescription.name}
                      </Typography>
                    </MemberName>
                  </LeftBlock>
                </Inner>
              </ContentWrapper>
              <ContentWrapper>
                <Inner>
                  <LeftBlock>
                    <List>
                      <ListItem>
                        <Typography bold size={1}>
                          id
                        </Typography>
                        <TextRightTypography>
                          {prescription.id}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          処方日時
                        </Typography>
                        <TextRightTypography>
                          {prescription.createdAt}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          アプリ名
                        </Typography>
                        <TextRightTypography>
                          {prescription.appName}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          処方医療機関
                        </Typography>
                        <TextRightTypography>
                          {prescription.medicalInstitutionName}
                        </TextRightTypography>
                      </ListItem>
                    </List>
                  </LeftBlock>
                  <RightBlock>
                    <List>
                      <ListItem>
                        <Typography bold size={1}>
                          生年月日
                        </Typography>
                        <TextRightTypography>
                          {prescription.birthday}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          処方コード
                        </Typography>
                        <TextRightTypography>
                          {prescription.code}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          処方コードID
                        </Typography>
                        <TextRightTypography>
                          {prescription.codeId}
                        </TextRightTypography>
                      </ListItem>
                      <ListItem>
                        <Typography bold size={1}>
                          処方コードステータス
                        </Typography>
                        <TextRightTypography>
                          {
                            StatusLabelMap[
                              prescription.codeStatus as StatusLabelForCsSystemType
                            ]
                          }
                        </TextRightTypography>
                      </ListItem>
                    </List>
                  </RightBlock>
                </Inner>
              </ContentWrapper>
              <SubmitButtonWrapper>
                <Button
                  onClick={() => {
                    onSubmitClick(prescription!.code);
                  }}
                  buttonType="primary"
                  disabled={isCancelPrescription}
                >
                  <Typography size={2}>
                    {isCancelPrescription
                      ? "処方キャンセル中..."
                      : "処方強制キャンセル"}
                  </Typography>
                </Button>
              </SubmitButtonWrapper>
            </>
          )}
        </Container>
      </PageBody>

      {!!snackBar && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
}
